import type { MouseEvent } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { SearchLg, FileSearch02, Menu02, List } from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { useDocsIndex, useDocsPage, useDocsSearchIndex, searchDocs } from "@/lib/docs";
import { highlightContainer } from "@/lib/codeHighlight";
import { sanitizeServerHtml } from "@/lib/sanitizeHtml";
import { useChangeLocale } from "@/lib/locale";

const inputCls =
  "w-full rounded-lg bg-primary px-3 py-2 text-sm text-primary shadow-xs ring-1 ring-primary outline-none focus:ring-2 focus:ring-brand";

/**
 * Křížové odkazy uvnitř nápovědy míří na cesty původního rozhraní — markdown je
 * sdílený oběma UI, takže je v SPA přepisujeme na vlastní routu. Bez toho klik
 * odvede uživatele plným načtením do `/admin/web/**`.
 */
const LEGACY_DOCS_PREFIX = "/admin/web/docs/";
const SPA_DOCS_PREFIX = "/app/docs/";

/**
 * Help center — třísloupcový layout: levá sekční nav (+ fulltextové vyhledávání),
 * střed = server-renderovaný Flexmark HTML (prose), pravý TOC z nadpisů stránky.
 * Obsah, filtraci i vyhledávací index dodává backend (`/admin/web/docs/*`);
 * SPA je jen prezentační vrstva. Route `/docs` zobrazí výchozí stránku,
 * `/docs/$slug` konkrétní.
 */
export function DocsPage() {
  const { t, i18n } = useTranslation();
  // Persistuje volbu k účtu — pouhé i18n.changeLanguage by se po refreshi vrátilo zpět.
  const changeLocale = useChangeLocale();
  const switchToCzech = () => changeLocale.mutate("cs");
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as { slug?: string };
  const index = useDocsIndex();
  const defaultSlug = index.data?.defaultSlug;
  const requestedSlug = params.slug ?? defaultSlug;
  const page = useDocsPage(requestedSlug);
  // Fallback: neznámý/chybný slug → výchozí stránka (pokud sama není chybná).
  const useFallback = !!requestedSlug && requestedSlug !== defaultSlug && page.isError && !!defaultSlug;
  const fallbackPage = useDocsPage(useFallback ? defaultSlug : undefined);
  const effectivePage = useFallback ? fallbackPage : page;
  const activeSlug = useFallback ? defaultSlug : requestedSlug;

  const [query, setQuery] = useState("");
  const [navOpen, setNavOpen] = useState(false);
  const [tocOpen, setTocOpen] = useState(false);
  const searchIndex = useDocsSearchIndex();
  const results = useMemo(() => searchDocs(searchIndex.data, query), [searchIndex.data, query]);

  const contentRef = useRef<HTMLDivElement>(null);
  const safeHtml = useMemo(
    () => (effectivePage.data ? sanitizeServerHtml(effectivePage.data.htmlContent) : ""),
    [effectivePage.data],
  );

  // CSP-safe syntax highlight po každém vložení server HTML.
  useEffect(() => {
    highlightContainer(contentRef.current);
  }, [safeHtml]);

  // Přepis odkazů na nápovědu z cest původního rozhraní na routu SPA.
  useEffect(() => {
    contentRef.current?.querySelectorAll<HTMLAnchorElement>(`a[href^="${LEGACY_DOCS_PREFIX}"]`).forEach((anchor) => {
      const target = anchor.getAttribute("href")!.slice(LEGACY_DOCS_PREFIX.length);
      anchor.setAttribute("href", SPA_DOCS_PREFIX + target);
    });
  }, [safeHtml]);

  /** Klik na přepsaný odkaz obslouží router — jinak by šlo o plné načtení stránky. */
  const handleContentClick = (event: MouseEvent<HTMLDivElement>) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    const anchor = (event.target as HTMLElement).closest("a");
    const href = anchor?.getAttribute("href");
    if (!anchor || anchor.target === "_blank" || !href?.startsWith(SPA_DOCS_PREFIX)) {
      return;
    }
    const [slug, hash] = href.slice(SPA_DOCS_PREFIX.length).split("#");
    if (!slug) {
      return;
    }
    event.preventDefault();
    void navigate({ to: "/docs/$slug", params: { slug }, hash: hash || undefined });
  };

  // Zavři mobilní panely při přechodu na jinou stránku.
  useEffect(() => {
    setNavOpen(false);
    setTocOpen(false);
  }, [activeSlug]);

  const headings = effectivePage.data?.headings ?? [];

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-display-sm font-semibold text-primary">{effectivePage.data?.title ?? t("docs.title")}</h1>
        <p className="mt-1 text-md text-tertiary">{t("docs.subtitle")}</p>
      </div>

      {/* Mobilní ovládání — nav / TOC toggly (skryté od lg / xl výš). */}
      <div className="flex flex-wrap gap-2 lg:hidden">
        <Button size="sm" color="secondary" iconLeading={Menu02} onPress={() => setNavOpen((v) => !v)}>
          {t("docs.nav.toggle")}
        </Button>
        {headings.length > 0 && (
          <Button size="sm" color="secondary" iconLeading={List} onPress={() => setTocOpen((v) => !v)}>
            {t("docs.toc.title")}
          </Button>
        )}
      </div>

      {/* Mobilní TOC (pod xl, kde není boční aside). */}
      {tocOpen && headings.length > 0 && (
        <nav className="rounded-xl bg-primary p-4 ring-1 ring-secondary xl:hidden">
          <p className="mb-2 text-xs font-semibold tracking-wide text-quaternary uppercase">{t("docs.toc.title")}</p>
          <ul className="flex flex-col gap-1">
            {headings.map((h) => (
              <li key={h.anchor}>
                <a
                  href={`#${h.anchor}`}
                  onClick={() => setTocOpen(false)}
                  className={`block py-0.5 text-sm text-tertiary hover:text-primary ${h.level >= 3 ? "pl-4" : ""}`}
                >
                  {h.text}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      )}

      <div className="flex flex-col gap-6 lg:flex-row lg:gap-8">
        {/* Levá nav + hledání (na mobilu skrytá, dokud navOpen). */}
        <nav className={`${navOpen ? "flex" : "hidden"} shrink-0 flex-col gap-3 lg:flex lg:w-64`}>
          <div className="relative">
            <SearchLg className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-tertiary" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("docs.search.placeholder")}
              className={inputCls + " pl-9"}
              aria-label={t("docs.search.placeholder")}
            />
          </div>

          {query.trim() ? (
            <div className="flex flex-col gap-1">
              {results.length === 0 && (
                <p className="px-3 py-2 text-sm text-tertiary">{t("docs.search.noResults")}</p>
              )}
              {results.map((r) => (
                <Link
                  key={r.slug}
                  to="/docs/$slug"
                  params={{ slug: r.slug }}
                  onClick={() => setQuery("")}
                  className="rounded-lg px-3 py-2 hover:bg-primary_hover"
                >
                  <span className="block text-sm font-medium text-primary">{r.title}</span>
                  <span className="block text-xs text-tertiary">{r.sectionTitle}</span>
                </Link>
              ))}
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {index.data?.sections.map((section) => (
                <div key={section.id}>
                  <p className="mb-1 px-3 text-xs font-semibold tracking-wide text-quaternary uppercase">
                    {section.title}
                  </p>
                  <div className="flex flex-col gap-0.5">
                    {section.pages.map((p) => {
                      const active = p.slug === activeSlug;
                      return (
                        <Link
                          key={p.slug}
                          to="/docs/$slug"
                          params={{ slug: p.slug }}
                          onClick={() => setNavOpen(false)}
                          className={`rounded-lg px-3 py-1.5 text-sm ${
                            active
                              ? "bg-primary_hover font-medium text-primary"
                              : "text-secondary hover:bg-primary_hover"
                          }`}
                        >
                          {p.title}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </nav>

        {/* Obsah */}
        <div className="min-w-0 flex-1">
          {(index.isLoading || effectivePage.isLoading) && <LoadingIndicator />}
          {(index.isError || effectivePage.isError) && !effectivePage.data && (
            <div className="flex flex-col items-center gap-2 rounded-xl py-16 text-center ring-1 ring-secondary">
              <FileSearch02 className="size-8 text-tertiary" />
              <p className="text-sm text-tertiary">{t("docs.notFound")}</p>
            </div>
          )}

          {effectivePage.data && (
            <article className="flex flex-col gap-4">
              {effectivePage.data.notTranslated && (
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-primary px-4 py-3 text-sm text-secondary ring-1 ring-secondary">
                  <span>{t("docs.banner.notTranslated")}</span>
                  {i18n.language !== "cs" && (
                    <Button size="sm" color="secondary" onPress={switchToCzech}>{t("docs.switchToCzech")}</Button>
                  )}
                </div>
              )}
              {effectivePage.data.lockedBanner && (
                <div className="rounded-lg px-4 py-3 text-sm text-warning-primary ring-1 ring-warning">
                  <strong className="mr-1 font-semibold">{t("docs.locked.title")}</strong>
                  {effectivePage.data.lockedBanner}
                </div>
              )}
              <div
                ref={contentRef}
                className="prose max-w-none prose-headings:scroll-mt-24"
                onClick={handleContentClick}
                // Obsah je server-renderovaný z našich vlastních markdown souborů;
                // DOMPurify je defenzivní pojistka.
                dangerouslySetInnerHTML={{ __html: safeHtml }}
              />
            </article>
          )}
        </div>

        {/* TOC (boční aside jen od xl výš; pod xl viz mobilní toggle nahoře). */}
        {headings.length > 0 && (
          <aside className="hidden shrink-0 xl:block xl:w-56">
            <div className="sticky top-6">
              <p className="mb-2 text-xs font-semibold tracking-wide text-quaternary uppercase">
                {t("docs.toc.title")}
              </p>
              <ul className="flex flex-col gap-1 border-l border-secondary">
                {headings.map((h) => (
                  <li key={h.anchor}>
                    <a
                      href={`#${h.anchor}`}
                      className={`block border-l-2 border-transparent py-0.5 text-sm text-tertiary hover:border-brand hover:text-primary ${
                        h.level >= 3 ? "pl-6" : "pl-3"
                      }`}
                    >
                      {h.text}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}
